/**
 * PWA Install Prompt Component
 * Shows a popup asking users to install the app on their home screen
 */
import React, { useState, useEffect } from 'react';
import { X, Download, Smartphone, Share, Plus } from 'lucide-react';
import { Button } from './ui/button';

const InstallPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed (standalone mode)
    const standalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone 
      || document.referrer.includes('android-app://');
    setIsStandalone(standalone);

    // Don't show if already installed
    if (standalone) {
      console.log('[InstallPrompt] App already installed, not showing prompt');
      return;
    }

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);
    console.log('[InstallPrompt] iOS detected:', iOS);

    // Check if user has dismissed the prompt before
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    const dismissedDate = dismissed ? new Date(dismissed) : null;
    const daysSinceDismissed = dismissedDate 
      ? (new Date() - dismissedDate) / (1000 * 60 * 60 * 24) 
      : null;

    // Show again after 7 days if dismissed
    if (dismissed && daysSinceDismissed < 7) {
      console.log('[InstallPrompt] Dismissed recently, not showing');
      return;
    }

    let promptShown = false;

    // Listen for the beforeinstallprompt event (Chrome, Edge, etc.)
    const handleBeforeInstall = (e) => {
      console.log('[InstallPrompt] beforeinstallprompt event fired');
      e.preventDefault();
      setDeferredPrompt(e);
      // Show prompt after a short delay
      if (!promptShown) {
        promptShown = true;
        setTimeout(() => setShowPrompt(true), 2000);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // Fallback: show custom prompt after timeout if beforeinstallprompt doesn't fire
    const fallbackTimer = setTimeout(() => {
      if (!promptShown) {
        console.log('[InstallPrompt] Fallback timer: showing prompt');
        promptShown = true;
        setShowPrompt(true);
      }
    }, iOS ? 3000 : 4000);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      clearTimeout(fallbackTimer);
    };
  }, []); // Empty dependency array - run only once on mount

  const handleInstall = async () => {
    if (deferredPrompt) {
      // Show the native install prompt
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        console.log('User accepted the install prompt');
      }
      setDeferredPrompt(null);
    }
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-install-dismissed', new Date().toISOString());
  };

  // Don't show if already installed or prompt shouldn't show
  if (isStandalone || !showPrompt) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-300">
      <div 
        className="bg-[#1a2332] border-2 border-[#FDB913] rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in slide-in-from-bottom-4 duration-300"
        data-testid="install-prompt"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-[#E10600] to-[#B30500] p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 p-2 rounded-xl">
              <Smartphone className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="text-white font-bold text-lg">Installeer F1 Pool</h2>
              <p className="text-white/80 text-sm">Voeg toe aan je startscherm</p>
            </div>
          </div>
          <button 
            onClick={handleDismiss}
            className="text-white/80 hover:text-white transition-colors p-1"
            aria-label="Sluiten"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5">
          {/* App Icon Preview */}
          <div className="flex items-center gap-4 mb-5 bg-[#0d1520] rounded-xl p-4">
            <img 
              src="/images/icon-96x96.png" 
              alt="F1 Pool App" 
              className="w-16 h-16 rounded-xl shadow-lg"
            />
            <div>
              <p className="text-white font-semibold">F1 Pool</p>
              <p className="text-gray-400 text-sm">Formule 1 Voorspellingen</p>
              <div className="flex items-center gap-1 mt-1">
                <span className="text-[#FDB913] text-xs">★★★★★</span>
                <span className="text-gray-500 text-xs">Gratis</span>
              </div>
            </div>
          </div>

          {/* Benefits */}
          <div className="space-y-3 mb-5">
            <div className="flex items-center gap-3 text-gray-300">
              <div className="bg-[#FDB913]/20 p-1.5 rounded-lg">
                <Download className="h-4 w-4 text-[#FDB913]" />
              </div>
              <span className="text-sm">Snelle toegang vanaf je startscherm</span>
            </div>
            <div className="flex items-center gap-3 text-gray-300">
              <div className="bg-[#FDB913]/20 p-1.5 rounded-lg">
                <Smartphone className="h-4 w-4 text-[#FDB913]" />
              </div>
              <span className="text-sm">Werkt als een echte app</span>
            </div>
          </div>

          {/* iOS Instructions */}
          {isIOS ? (
            <div className="bg-[#0d1520] rounded-xl p-4 mb-4">
              <p className="text-white font-medium mb-3">Zo installeer je de app:</p>
              <div className="space-y-2">
                <div className="flex items-center gap-3 text-gray-300 text-sm">
                  <div className="bg-blue-500/20 p-1.5 rounded-lg">
                    <Share className="h-4 w-4 text-blue-400" />
                  </div>
                  <span>1. Tik op het <strong>Deel</strong>-icoon onderaan</span>
                </div>
                <div className="flex items-center gap-3 text-gray-300 text-sm">
                  <div className="bg-blue-500/20 p-1.5 rounded-lg">
                    <Plus className="h-4 w-4 text-blue-400" />
                  </div>
                  <span>2. Kies <strong>"Zet op beginscherm"</strong></span>
                </div>
              </div>
            </div>
          ) : !deferredPrompt ? (
            <div className="bg-[#0d1520] rounded-xl p-4 mb-4">
              <p className="text-white font-medium mb-3">Zo installeer je de app:</p>
              <div className="space-y-2">
                <div className="flex items-center gap-3 text-gray-300 text-sm">
                  <div className="bg-blue-500/20 p-1.5 rounded-lg">
                    <span className="text-blue-400 font-bold text-xs">⋮</span>
                  </div>
                  <span>1. Klik op het <strong>menu</strong> (⋮) rechtsboven</span>
                </div>
                <div className="flex items-center gap-3 text-gray-300 text-sm">
                  <div className="bg-blue-500/20 p-1.5 rounded-lg">
                    <Download className="h-4 w-4 text-blue-400" />
                  </div>
                  <span>2. Kies <strong>"Installeren"</strong> of <strong>"Toevoegen aan startscherm"</strong></span>
                </div>
              </div>
            </div>
          ) : null}

          {/* Buttons */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={handleDismiss}
              className="flex-1 border-gray-600 text-gray-300 hover:bg-gray-800"
            >
              Later
            </Button>
            {deferredPrompt && (
              <Button
                onClick={handleInstall}
                className="flex-1 bg-[#FDB913] text-[#1a2332] hover:bg-[#e5a911] font-semibold"
                data-testid="install-btn"
              >
                <Download className="h-4 w-4 mr-2" />
                Installeren
              </Button>
            )}
            {!deferredPrompt && (
              <Button
                onClick={handleDismiss}
                className="flex-1 bg-[#FDB913] text-[#1a2332] hover:bg-[#e5a911] font-semibold"
              >
                Begrepen
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstallPrompt;
